// 1. Framework / library imports
import { memo, useCallback, useEffect, useState } from "react";

// 2. Helpers & Hooks
import { useTranslate } from "helpers";
import {
  useHandleDateChange,
  useHandleDropdownChange,
  useHandleTextFieldChange,
  useHandleNumberFieldChange,
} from "services/forms/hooks";

// 3. API & Types
import { getFleetVehicleColorAPI } from "services/api/fleet/vehicles/api";
import { FleetVehicleColor } from "services/api/fleet/vehicles/types";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";

// 4. UI Components
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import VehicleStatusDropdown from "ui/components/Dropdown/VehicleStatusDropdown/VehicleStatusDropdown";
import { TextField } from "ui/components/TextField/TextField";
import NumberField from "ui/components/TextField/NumberField/NumberField";
import { VinTextField } from "ui/components/TextField/VinTextField";
import { DatePicker } from "ui/components/DatePicker";

// 5. Local types
import { VehicleInfoFormProps } from "./types";
import ContactDropdown from "ui/components/Dropdown/ContactDropdown/ContactDropdown";

const VehicleInfoForm: React.FC<VehicleInfoFormProps> = (props) => {
  const { formValues, setFormValues, errors, setVehicleInfo } = props;

  const { t } = useTranslate();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [fleetVehicleColor, setFleetVehicleColor] = useState<
    FleetVehicleColor[]
  >([]);

  useEffect(() => {
    fetchFleetVehicleColor();
  }, [formValues.id]);

  // Sync vehicleInfo with form fields
  useEffect(() => {
    if (!setVehicleInfo) return;
    setVehicleInfo((prev) => ({
      ...prev,
      vinNumber: formValues.vinNumber || null,
      kmTraveled: formValues.kmTraveled ? Number(formValues.kmTraveled) : null,
      registrationPlate: formValues.registrationPlate || null,
    }));
  }, [formValues.vinNumber, formValues.kmTraveled, formValues.registrationPlate, setVehicleInfo]);

  const fetchFleetVehicleColor = async () => {
    setIsLoading(true);

    let result;

    try {
      result = await getFleetVehicleColorAPI();
    } catch {
      setIsLoading(false);
      return;
    }

    setFleetVehicleColor(result);
    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleDateChange = useHandleDateChange(setFormValues, formValues);

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues
  );

  const handleNumberFieldChange = useHandleNumberFieldChange(
    setFormValues,
    formValues
  );

  // Dropdown radi s listom, a vozilo ima najviše jedan status.
  const statusValue = formValues.status ? [formValues.status] : [];

  const handleStatusChange = useCallback(
    (_name: string, statuses: string[]) => {
      setFormValues((prev) => ({ ...prev, status: statuses[0] ?? null }));
    },
    [setFormValues]
  );

  const vehicleColorItems: DropdownOption[] = fleetVehicleColor.map((i) => {
    return {
      value: i.id,
      color: i.id,
      text: i.name,
    };
  });

  return (
    <>
      <div className="flex items-center justify-center px-4  pt-12 pb-6">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">{t("admin.fleet.vehicle.edit.info.title")}</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">Unesite osnovne informacije o vozilu kako biste nastavili s konfiguracijom. </p>
          <form noValidate className="border bg-white border-slate-200">
            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{t("admin.fleet.vehicle.add.basicinfo.title")}</h3>
                <p className="text-xs text-slate-500">Identificirajte vozilo i zabilježite trenutno stanje</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <NumberField
                    name="kmTraveled"
                    label={t("admin.fleet.vehicle.add.kmtraveled")}
                    placeholder={t(
                      "admin.fleet.vehicle.add.kmtraveled.placeholder"
                    )}
                    onChange={handleNumberFieldChange}
                    error={errors.kmTraveled}
                    value={formValues.kmTraveled}
                    required
                    decimals={0}
                    groupAppend="km"
                  />
                </div>
                <div>
                  <VinTextField
                    name="vinNumber"
                    label={t("admin.fleet.vehicle.add.vin")}
                    placeholder={t("admin.fleet.vehicle.add.vin.placeholder")}
                    onChange={handleTextFieldChange}
                    error={errors.vinNumber}
                    value={formValues.vinNumber}
                  />
                </div>
                <div>
                  <VehicleStatusDropdown
                    name="status"
                    label={t("admin.fleet.vehicle.status.modal.title")}
                    placeholder="Odaberite status"
                    value={statusValue}
                    onChange={handleStatusChange}
                    error={errors.status}
                    required
                    singleSelect
                  />
                </div>
              </div>
            </div>

            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{t("admin.fleet.vehicle.add.registration.title")}</h3>
                <p className="text-xs text-slate-500">Podaci o registraciji i valjanosti prometne dozvole</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <TextField
                    type="text"
                    name="registrationPlate"
                    label={t("admin.fleet.vehicle.add.registrationnumber")}
                    placeholder={t(
                      "admin.fleet.vehicle.add.registrationnumber.placeholder"
                    )}
                    onChange={handleTextFieldChange}
                    error={errors.registrationPlate}
                    value={formValues.registrationPlate}
                  ></TextField>
                </div>
                <div>
                  <DatePicker
                    name="registrationExpiration"
                    label={t("admin.fleet.vehicle.add.registrationexpiration")}
                    placeholder={t(
                      "admin.fleet.vehicle.add.registrationexpiration.placeholder"
                    )}
                    onChange={handleDateChange}
                    error={errors.registrationExpiration}
                    value={formValues.registrationExpiration}
                    width="full"
                  />
                </div>
                <div>
                  <DatePicker
                    name="firstRegistrationDate"
                    label={t("admin.fleet.vehicle.add.firstregistrationdate")}
                    placeholder={t(
                      "admin.fleet.vehicle.add.firstregistrationdate.placeholder"
                    )}
                    onChange={handleDateChange}
                    error={errors.firstRegistrationDate}
                    value={formValues.firstRegistrationDate}
                    width="full"
                  />
                </div>
              </div>
            </div>

            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{t("admin.fleet.vehicle.add.warranty.title")}</h3>
                <p className="text-xs text-slate-500">Pratite pokrivenost garancijom i uvjete korištenja</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <DatePicker
                    name="warrantyExpirationDate"
                    label={t("admin.fleet.vehicle.add.warrantyexpirationdate")}
                    placeholder={t(
                      "admin.fleet.vehicle.add.warrantyexpirationdate.placeholder"
                    )}
                    onChange={handleDateChange}
                    error={errors.warrantyExpirationDate}
                    value={formValues.warrantyExpirationDate}
                    width="full"
                  />
                </div>
                <div className="col-span-2">
                  <TextField
                    type="number"
                    name="warrantyMileageLimit"
                    label={t("admin.fleet.vehicle.add.warrantymileagelimit")}
                    placeholder={t(
                      "admin.fleet.vehicle.add.warrantymileagelimit.placeholder"
                    )}
                    onChange={handleTextFieldChange}
                    error={errors.warrantyMileageLimit}
                    value={formValues.warrantyMileageLimit}
                    groupAppend="km"
                  ></TextField>
                </div>
              </div>
            </div>

            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{t("admin.fleet.vehicle.add.insurance.title")}</h3>
                <p className="text-xs text-slate-500">Upravljajte policom obveznog osiguranja vozila</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <DatePicker
                    name="autoInsuranceExpiryDate"
                    label={t("admin.fleet.vehicle.add.autoinsuranceexpirydate")}
                    placeholder={t(
                      "admin.fleet.vehicle.add.autoinsuranceexpirydate.placeholder"
                    )}
                    onChange={handleDateChange}
                    error={errors.autoInsuranceExpiryDate}
                    value={formValues.autoInsuranceExpiryDate}
                    width="full"
                  />
                </div>
                <div className="col-span-2">
                  <ContactDropdown
                    placeholder={t(
                      "admin.fleet.vehicle.add.autoinsurancecontact.placeholder"
                    )}
                    label={t("admin.fleet.vehicle.add.autoinsurancecontact")}
                    name="autoInsuranceContactId"
                    value={formValues.autoInsuranceContactId}
                    onChange={handleDropdownChange}
                    error={errors.autoInsuranceContact}
                  />
                </div>
              </div>
            </div>

            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{t("admin.fleet.vehicle.add.casco.title")}</h3>
                <p className="text-xs text-slate-500">Upravljajte kasko policom za dodatnu zaštitu vozila</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <DatePicker
                    name="cascoExpiryDate"
                    label={t("admin.fleet.vehicle.add.cascoexpirydate")}
                    placeholder={t(
                      "admin.fleet.vehicle.add.cascoexpirydate.placeholder"
                    )}
                    onChange={handleDateChange}
                    error={errors.cascoExpiryDate}
                    value={formValues.cascoExpiryDate}
                    width="full"
                  />
                </div>
                <div className="col-span-2">
                  <ContactDropdown
                    placeholder={t(
                      "admin.fleet.vehicle.add.cascocontact.placeholder"
                    )}
                    label={t("admin.fleet.vehicle.add.cascocontact")}
                    name="cascoContactId"
                    value={formValues.cascoContactId}
                    onChange={handleDropdownChange}
                    error={errors.cascoContact}
                  />
                </div>
              </div>
            </div>

            <div className="py-6 px-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{t("admin.fleet.vehicle.add.additionalinfo.title")}</h3>
                <p className="text-xs text-slate-500">Vizualne karakteristike i ostale napomene o vozilu</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Dropdown
                    name="vehicleColorId"
                    label={t("admin.fleet.vehicle.add.color")}
                    placeholder={t("admin.fleet.vehicle.add.color.placeholder")}
                    options={vehicleColorItems}
                    value={formValues.vehicleColorId}
                    onChange={handleDropdownChange}
                  />
                </div>
                <div className="col-span-2">
                  <TextField
                    type="text"
                    name="colorName"
                    label={t("admin.fleet.vehicle.add.colorname")}
                    placeholder={t("admin.fleet.vehicle.add.colorname.placeholder")}
                    onChange={handleTextFieldChange}
                    error={errors.colorName}
                    value={formValues.colorName}
                  ></TextField>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>


    </>
  );
};
export default memo(VehicleInfoForm);
